import { z } from 'zod';
import { KPI_AGGREGATIONS, KPI_DIRECTIONS, type KpiPeriodicityName } from './kpis';
import { TARGET_APPROVAL_STATUSES } from './targets';
import { LINEAR_WINDOW, PROJECTION_METHODS, periodMonthsOf, type ProjectionMethodName } from './planned';
import { TRAFFIC_LIGHTS } from './status';

// ---------------------------------------------------------------------------------------------
// Planejamento de longo prazo e projeções (T-181, RF-29, RF-26, PRD §11.17; DEC-064)

const kpiRef = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  unitLabel: z.string(),
  aggregation: z.enum(KPI_AGGREGATIONS),
  perspectiveId: z.string().uuid(),
  perspectiveName: z.string(),
  departmentId: z.string().uuid(),
});

/** Uma casa da tabela ano × indicadores: a meta oficial do ano (empresa) e o realizado do ano. */
export const longTermCellSchema = z.object({
  kpiId: z.string().uuid(),
  year: z.number().int(),
  /** Meta do ano: a cadastrada como "Ano" ou, sem ela, o acumulado das metas dos períodos (pela agregação). */
  target: z.number().nullable(),
  /** YEAR: meta do ano cadastrada; PERIODS: acumulado das metas dos meses/períodos; null: sem meta. */
  targetSource: z.enum(['YEAR', 'PERIODS']).nullable(),
  /** Realizado acumulado do ano até o último período com resultado (null sem resultado). */
  actual: z.number().nullable(),
  lastActualMonth: z.number().int().nullable(),
  /**
   * Versão vigente da meta "Ano" (empresa), em qualquer estado — com o fluxo de aprovação, a pendente ou a
   * rejeitada ainda não vale no `target`. null: nunca houve meta "Ano" neste KPI e ano.
   */
  yearTarget: z
    .object({ value: z.number().nullable(), approvalStatus: z.enum(TARGET_APPROVAL_STATUSES) })
    .nullable(),
});
export type LongTermCell = z.infer<typeof longTermCellSchema>;

export const longTermSchema = z.object({
  years: z.array(z.number().int()),
  currentYear: z.number().int(),
  kpis: z.array(kpiRef),
  cells: z.array(longTermCellSchema),
});
export type LongTerm = z.infer<typeof longTermSchema>;

// --- Projeções ---------------------------------------------------------------------------------

export const PROJECTION_LIGHTS = TRAFFIC_LIGHTS;

/** Períodos com realizado que a projeção usou (o intervalo da base de cálculo). */
export const projectionBasisSchema = z.object({
  fromMonth: z.number().int().min(1).max(12),
  toMonth: z.number().int().min(1).max(12),
  periods: z.number().int().min(1),
});
export type ProjectionBasis = z.infer<typeof projectionBasisSchema>;

const methodResultSchema = z.object({
  method: z.enum(PROJECTION_METHODS),
  closing: z.number().nullable(),
  closingPerformance: z.number().nullable(),
  basis: projectionBasisSchema.nullable(),
});

export const projectionItemSchema = z.object({
  kpi: kpiRef.extend({ direction: z.enum(KPI_DIRECTIONS) }),
  annualTarget: z.number().nullable(),
  ytdActual: z.number().nullable(),
  lastActualMonth: z.number().int().nullable(),
  /** Método usado no fechamento (tendência com 2 ou mais períodos; senão a média) — null sem realizado. */
  method: z.enum(PROJECTION_METHODS).nullable(),
  closing: z.number().nullable(),
  closingPerformance: z.number().nullable(),
  light: z.enum(PROJECTION_LIGHTS),
  basis: projectionBasisSchema.nullable(),
  /** Faixa do fechamento entre os métodos (menor e maior); null sem nenhuma projeção. */
  range: z.object({ low: z.number(), high: z.number() }).nullable(),
  methods: z.array(methodResultSchema),
});
export type ProjectionItem = z.infer<typeof projectionItemSchema>;

export const projectionsQuerySchema = z.object({
  /** Sem ano: o corrente, se estiver no horizonte; senão o primeiro ano do horizonte. */
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  perspectiveId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
});

export const projectionsSchema = z.object({
  year: z.number().int(),
  years: z.array(z.number().int()),
  perspectives: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  items: z.array(projectionItemSchema),
});
export type Projections = z.infer<typeof projectionsSchema>;

/**
 * Intervalo da base de cálculo de um método (DEC-038/DEC-064): os períodos do ano com realizado que entram na
 * projeção — na tendência, os últimos até 6 (precisa de 2); na média, todos. null quando o método não projeta.
 */
export function projectionBasis(
  actuals: (number | null)[],
  periodicity: KpiPeriodicityName,
  method: ProjectionMethodName,
): ProjectionBasis | null {
  const realized = periodMonthsOf(periodicity).filter((m) => {
    const v = actuals[m - 1];
    return v !== null && v !== undefined && Number.isFinite(v);
  });
  const used = method === 'LINEAR' ? realized.slice(-LINEAR_WINDOW) : realized;
  if (!used.length || (method === 'LINEAR' && used.length < 2)) return null;
  return { fromMonth: used[0]!, toMonth: used.at(-1)!, periods: used.length };
}

/** Método do fechamento: a tendência quando ela projeta; senão a média; null quando nenhum projeta. */
export function chosenProjection<T extends { method: ProjectionMethodName; closing: number | null }>(
  methods: T[],
): T | null {
  return (
    methods.find((m) => m.method === 'LINEAR' && m.closing !== null) ??
    methods.find((m) => m.closing !== null) ??
    null
  );
}

/** Faixa do fechamento entre os métodos que projetam. */
export function projectionRange(closings: (number | null)[]): { low: number; high: number } | null {
  const values = closings.filter((v): v is number => v !== null);
  return values.length ? { low: Math.min(...values), high: Math.max(...values) } : null;
}
